import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Switch,
  TablePagination,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SourceChannelFilters from "../components/sourceChannels/SourceChannelFilters";
import SourceChannelTable from "../components/sourceChannels/SourceChannelTable";
import SourceChannelDetailDialog from "../components/sourceChannels/SourceChannelDetailDialog";
import {
  fetchSourceChannels,
  selectSourceChannelsError,
  selectSourceChannelsFilters,
  selectSourceChannelsLoading,
  selectSourceChannelsMeta,
  selectSourceChannelsPagination,
  selectSourceChannelsSort,
  setSourcePage,
  setSourcePageSize,
} from "../store/slices/sourceChannelsSlice";

const SourceChannelListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pagination = useSelector(selectSourceChannelsPagination);
  const filters = useSelector(selectSourceChannelsFilters);
  const sort = useSelector(selectSourceChannelsSort);
  const meta = useSelector(selectSourceChannelsMeta);
  const loading = useSelector(selectSourceChannelsLoading);
  const error = useSelector(selectSourceChannelsError);
  const [dense, setDense] = useState(false);

  useEffect(() => {
    dispatch(fetchSourceChannels());
  }, [
    dispatch,
    filters.search,
    filters.status,
    sort.field,
    sort.direction,
    pagination.page,
    pagination.pageSize,
  ]);

  const total = useMemo(() => meta?.totalItems ?? 0, [meta?.totalItems]);

  useEffect(() => {
    if (!meta?.totalPages) return;
    const maxPage = Math.max(0, meta.totalPages - 1);
    if (pagination.page > maxPage) {
      dispatch(setSourcePage(maxPage));
    }
  }, [dispatch, meta, pagination.page]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator=">"
            sx={{ fontSize: 12, mb: 1, color: "text.secondary" }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Source Channel</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Source Channels
          </Typography>
          <Typography color="text.secondary">
            Manage source channel credentials and activation status.
          </Typography>
        </Box>
        <Button
          size="large"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/source-channels/new")}
          sx={{
            alignSelf: { xs: "stretch", md: "center" },
            width: { xs: "100%", sm: "auto" },
            maxWidth: { xs: "calc(100vw - 32px)", sm: 240 },
            boxSizing: "border-box",
          }}
        >
          New Source Channel
        </Button>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
        }}
      >
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <SourceChannelFilters />

        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            borderRadius: 1,
            mt: 3,
            backgroundColor: "background.paper",
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(148, 163, 184, 0.35)",
              borderRadius: 999,
            },
          }}
        >
          <SourceChannelTable dense={dense} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={(event) => setDense(event.target.checked)}
              />
            }
            label="Dense"
          />
          <TablePagination
            component="div"
            count={total}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            page={pagination.page}
            onPageChange={(_, newPage) => dispatch(setSourcePage(newPage))}
            onRowsPerPageChange={(event) => {
              const nextRows = parseInt(event.target.value, 10);
              dispatch(setSourcePageSize(nextRows));
            }}
            sx={{
              "& .MuiTablePagination-toolbar": {
                px: { xs: 0, sm: 2 },
              },
            }}
          />
        </Stack>
      </Paper>

      <SourceChannelDetailDialog />
    </Stack>
  );
};

export default SourceChannelListPage;
